import React, { useState, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import rammir from "../images/rammir.jpg";

function About() {
  const controls = useAnimation();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [activeTab, setActiveTab] = useState("about");

  useEffect(() => {
    if (inView) controls.start("visible");
  }, [controls, inView]);

  const containerVariants = {
    visible: { transition: { staggerChildren: 0.2 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
  };

  const educationList = [
    { title: "Bachelor’s Degree in Information Systems", place: "Camarines Norte State College" },
    { title: "High School Diploma", place: "Paracale National High School" },
    { title: "CITI Graduate (Web Development)", place: "Labo, Camarines Norte" },
  ];

  return (
    <section
      id="about"
      ref={ref}
      className="relative min-h-screen flex flex-col items-center justify-center snap-start px-6 py-16 text-white overflow-visible"
    >
      {/* Title */}
      <motion.h2
        className="text-4xl md:text-6xl font-extrabold text-green mb-12 relative text-center"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: inView ? 1 : 0, y: inView ? 0 : -50 }}
        transition={{ duration: 1 }}
      >
        Profile
        <span
          className="absolute inset-0 blur-2xl opacity-50 animate-glowPulse"
          style={{ background: "radial-gradient(circle, #00ff00 0%, transparent 70%)" }}
        />
      </motion.h2>

      {/* Desktop Layout */}
      <motion.div
        className="hidden md:flex w-full max-w-6xl gap-16"
        variants={containerVariants}
        initial="hidden"
        animate={controls}
      >
        {/* Left: Profile */}
        <motion.div
          className="flex flex-col items-center text-center md:text-left md:items-start rounded-xl p-8 shadow-lg flex-1"
          variants={itemVariants}
        >
          <img
            src={rammir}
            alt="Rammir"
            className="rounded-full w-40 h-40 object-cover border-4 border-green shadow-lg mb-6"
          />
          <p className="font-pixelBody text-gray-400 mb-4">
            Hi, I’m Rammir R. Timbas, a passionate developer creating elegant web solutions. My expertise lies in Web Development, Enterprise Systems, and Mobile Applications.
          </p>
          <p className="font-pixelBody text-gray-400">
            Outside of work, I enjoy Japanese anime, mobile/PC games, and social media.
          </p>
        </motion.div>

        {/* Right: Education */}
        <motion.div className="flex flex-col gap-6 flex-1" variants={itemVariants}>
          <h3 className="font-pixelTitle text-3xl font-semibold text-green mb-4 text-center md:text-left">
            Education
          </h3>
          <div className="flex flex-col gap-4">
            {educationList.map((edu, i) => (
              <motion.div
                key={i}
                className="relative pl-10 border-l-2 border-green"
                variants={itemVariants}
              >
                <div className="absolute w-5 h-5 bg-green rounded-full -left-3 top-1.5" />
                <h4 className="font-pixelBody text-lg font-bold">{edu.title}</h4>
                <p className="font-pixelBody text-gray-400">{edu.place}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </motion.div>

      {/* Mobile Layout */}
      <div className="md:hidden w-full max-w-md mt-8">
        {/* Tabs */}
        <div className="flex justify-center mb-6 gap-4">
          <button
            onClick={() => setActiveTab("about")}
            className={`px-4 py-2 rounded-full text-sm font-medium ${
              activeTab === "about" ? "bg-green text-black" : "bg-gray-800 text-white"
            }`}
          >
            About Me
          </button>
          <button
            onClick={() => setActiveTab("education")}
            className={`px-4 py-2 rounded-full text-sm font-medium ${
              activeTab === "education" ? "bg-green text-black" : "bg-gray-800 text-white"
            }`}
          >
            Education
          </button>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === "about" && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="text-center flex flex-col items-center"
            >
              <motion.img
                src={rammir}
                alt="Rammir"
                className="mx-auto rounded-full w-32 h-32 object-cover border-4 border-green shadow-lg mb-4"
                variants={itemVariants}
              />
              <motion.p className="font-pixelBody text-gray-400 mb-2" variants={itemVariants}>
                Hi, I’m Rammir R. Timbas, a passionate developer creating elegant web solutions. My expertise lies in Web Development, Enterprise Systems, and Mobile Applications.
              </motion.p>
              <motion.p className="font-pixelBody text-gray-400" variants={itemVariants}>
                Outside of work, I enjoy Japanese anime, mobile/PC games, and social media.
              </motion.p>
            </motion.div>
          )}

          {activeTab === "education" && (
            <motion.div
              className="space-y-4"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {educationList.map((edu, i) => (
                <motion.div key={i} className="relative pl-6 border-l-2 border-green" variants={itemVariants}>
                  <div className="absolute w-4 h-4 bg-green rounded-full -left-2 top-1.5" />
                  <h4 className="font-pixelBody text-lg font-bold">{edu.title}</h4>
                  <p className="font-pixelBody text-gray-400">{edu.place}</p>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}

export default About;
